import React, { useMemo, memo, useState, ReactNode, Dispatch, SetStateAction } from 'react';
import Button from '@mui/material/Button';
import formatCurrency from './Formatting';

import { MaterialReactTable, createMRTColumnHelper, useMaterialReactTable, MRT_SortingFn, MRT_Row, MRT_SortingState, MRT_ColumnFiltersState } from 'material-react-table';
import { MRT_Localization_SV } from 'material-react-table/locales/sv';

import { createTheme, ThemeProvider, useTheme } from '@mui/material';
import { svSE } from '@mui/material/locale';

import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DraftFunction } from 'use-immer';

import { CustomerPaymentInfo, Expense, Income, OverviewData, SupplierPaymentInfo } from './Types';
import { apiSendReminder } from './Api';
import { useHasCapability } from './Capabilities';
import ConfirmDialog from './ConfirmDialog';

function createListFormatter<Value>({textAlign, width, elementFormatter}: {
    textAlign: string,
    width: string | number,
    elementFormatter: (value: Value) => ReactNode
}): (value: {cell: {getValue: () => Value | Value[]}}) => ReactNode {
    return ({ cell }) => {
        const value = cell.getValue()
        if (!Array.isArray(value)) {
            return elementFormatter(value)
        }
        return <Box
          sx={{
            display: 'flex',
            flexFlow: 'column nowrap',
            textAlign: textAlign,
          }}
        >
            { value.map((local_row, index) => <Box
                key={index}
                sx={{
                    width: width,
                    whiteSpace: (width == 'auto') ? 'nowrap' : 'wrap',
                    overflowWrap: 'break-word'
                }}>{elementFormatter(local_row)}</Box>) }
        </Box>
    }
}

function createListFormatterPre<Value>({textAlign, width, elementFormatter}: {
    textAlign: string,
    width: string | number,
    elementFormatter: (value: Value) => ReactNode
}): (value: Value | Value[]) => ReactNode {
    return value => {
        if (!Array.isArray(value)) {
            return elementFormatter(value)
        }
        return <Box
          sx={{
            display: 'flex',
            flexFlow: 'column nowrap',
            textAlign: textAlign,
          }}
        >
            { value.map((local_row, index) => <Box
                key={index}
                sx={{
                    width: width,
                    whiteSpace: (width == 'auto') ? 'nowrap' : 'wrap',
                    overflowWrap: 'break-word'
                }}>{elementFormatter(local_row)}</Box>) }
        </Box>
    }
}

function sendReminder(row_index: number, customerInvoiceId: string, setIncomes: (func: DraftFunction<Income[]>) => void): Promise<void> {
    return apiSendReminder({'customer_invoice_id': customerInvoiceId})
    .then(() => {
        setIncomes((incomes) => {
            const paymentInfo = incomes[row_index].PaymentInfo
            if (paymentInfo !== null) paymentInfo.PaymentReminderIssued = true
        })
    })
    .catch(() => {})  // the failure is already on the error bus (parseResponse); just stop spinning
}

// Self-contained so a reminder shows a spinner and disables while in flight, instead
// of a live button with no feedback (and double-click protection for free).
function ReminderButton({rowIndex, customerInvoiceId, alreadyIssued, tooltipText, customerName, setIncomes}: {
    rowIndex: number,
    customerInvoiceId: string,
    alreadyIssued: boolean,
    tooltipText: string,
    customerName: string,
    setIncomes: (func: DraftFunction<Income[]>) => void,
}) {
    const canRemind = useHasCapability('customer_invoice.remind')
    const [busy, setBusy] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const onConfirm = () => {
        setBusy(true)
        sendReminder(rowIndex, customerInvoiceId, setIncomes).finally(() => { setBusy(false); setConfirmOpen(false) })
    }
    if (!canRemind) return null
    return <>
        <Button variant="outlined" size="small" onClick={() => setConfirmOpen(true)} disabled={busy} title={tooltipText}>
            {busy ? <CircularProgress size="1em"/> : (alreadyIssued ? 'Påminn igen…' : 'Påminn…')}
        </Button>
        <ConfirmDialog
            open={confirmOpen}
            title="Skicka betalningspåminnelse?"
            message={`En betalningspåminnelse för ${tooltipText} skickas via e-post till ${customerName}.`}
            confirmLabel="Skicka påminnelse"
            busy={busy}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={onConfirm}
        />
    </>
}


function incomePaymentInfoToInt(paymentInfo: CustomerPaymentInfo | null) {
    if (paymentInfo === null) {
        return 10
    }
    if (paymentInfo.IsCreditInvoice) {
        return 9
    }
    if (paymentInfo.PaymentStatus == 0) {
        return 8
    }
    if (paymentInfo.PaymentStatus == 1) {
        return 7
    }
    return paymentInfo.PaymentReminderIssued ? 6 : 5;
}

const incomePaymentInfoSorter: MRT_SortingFn<PreprocessedIncome> = (aRow, bRow, columnId) => {
//function incomePaymentInfoSorter(aRow: MRT_Row<CustomerPaymentInfo>, bRow: MRT_Row<CustomerPaymentInfo>, columnId: string) {
    const a = aRow.getValue<CustomerPaymentInfo | null>(columnId)
    const b = bRow.getValue<CustomerPaymentInfo | null>(columnId)
    return incomePaymentInfoToInt(a) - incomePaymentInfoToInt(b)
}

function expensePaymentInfoFormatter({row}: {row: MRT_Row<Expense>}) {
    var paymentInfo = row.getValue<SupplierPaymentInfo | null>('PaymentInfo');
    if (paymentInfo === null) {
        return null
    }
    if (paymentInfo.RemainingAmount == 0) {
        return 'Betald'
    }
    const text = paymentInfo.IsOverdue ? 'Förfallen' : 'Obetald'
    var tooltipText = formatCurrency(-paymentInfo.RemainingAmount) + ' kr senast ' + paymentInfo.DueDate
    return <span className="tooltip" title={tooltipText}>{text}</span>
}

const expensePaymentInfoSorter: MRT_SortingFn<Expense> = (aRow, bRow, columnId) => {    
    //if (columnId !== 'PaymentInfo') {
    //    throw new Error(`columnId was '${columnId}' but should have been 'PaymentInfo'`);
    //}

    const a = aRow.getValue<SupplierPaymentInfo | null>(columnId)
    const b = bRow.getValue<SupplierPaymentInfo | null>(columnId)
    function f() {
        if (a === null) {
            if (b === null) {
                return 0;
            }
            return 1;
        }
        if (b === null) {
            return -1
        }
        if (a.PaymentStatus == b.PaymentStatus) {
            return 0;
        }
        if (a.PaymentStatus < b.PaymentStatus) {
            return -1;
        }
        return 1;
    }
//    console.log("a: " + a.PaymentStatus + "; b: " + b.PaymentStatus + "; " + f())
    return f()
}

type RowType = {getValue<T>(columnId: string): T}
function sumSorter(aRow: RowType, bRow: RowType, columnId: string): number {
    const a = aRow.getValue<number[]>(columnId)
    const b = bRow.getValue<number[]>(columnId)
    console.log('sumSorter(' + a + ', ' + b + ')');
    let sum = 0.0;
    a.forEach(x => { sum += x; });
    b.forEach(x => { sum -= x; });
//    console.log('sum: ' + sum);
    return sum;
}

/**
 * Retrieve the array key corresponding to the largest element in the array.
 *
 * @param {Array.<number>} array Input array
 * @return {number} Index of array element with largest value
 */
function argMax(array: number[]): number {
    return array.map((x, i) => [x, i]).reduce((r, a) => (a[0] > r[0] ? a : r))[1];
}

function categorySorter(aRow: RowType, bRow: RowType, columnId: string): number {    
    function getMostImportantIndex(row: RowType) {
        const amounts = row.getValue<number[]>('IncomeAmount') // TODO: This should be 'ExpenseAmount' sometimes.
        return argMax(amounts)
    }
    const alpha = aRow.getValue<string[]>(columnId)[getMostImportantIndex(aRow)]
    const beta = bRow.getValue<string[]>(columnId)[getMostImportantIndex(bRow)]
    return alpha.localeCompare(beta, 'sv-SE')
}

function firstSorter(aRow: RowType, bRow: RowType, columnId: string): number {
    let a = aRow.getValue<string[] | string>(columnId)
    let b = bRow.getValue<string[] | string>(columnId)

    if (typeof a === 'string') {
        a = [a]
    }
    if (typeof b === 'string') {
        b = [b]
    }
    if (a.length == 0) {
        if (b.length == 0) {
            return 0;
        }
        return 1;
    }
    if (b.length == 0) {
        return -1
    }
    return a[0].localeCompare(b[0], 'sv-SE');
}

const localeSortFn = (aRow: RowType, bRow: RowType, columnId: string): number => {
    const a = aRow.getValue<string | number>(columnId)
    const b = bRow.getValue<string | number>(columnId)
    return ('' + a).localeCompare('' + b, 'sv-SE');
}

function createListFilterFn<Value>(elementFilter: (value: Value, filterValue: string) => boolean) {
    return (row: RowType, columnId: string, filterValue: string) => {
        const value = row.getValue<Value | Value[] | undefined>(columnId)
        if (value === undefined) {
            return false
        }
        if (Array.isArray(value)) {
            return value.some(s => elementFilter(s, filterValue))
        }
        return elementFilter(value, filterValue)
    }
}

function textFilter(value: string, filterValue: string): boolean {
    return value.toLowerCase().includes(filterValue.toLowerCase())
}

function amountFilter(value: number, filterValue: string): boolean {
    return '' + value === filterValue
}


const createIncomePaymentInfoFormatter = ((setIncomes: (func: DraftFunction<Income[]>) => void) =>
    ({row}: {row: MRT_Row<PreprocessedIncome>}) => {
        const paymentInfo = row.getValue<CustomerPaymentInfo | null>('PaymentInfo');
        if (paymentInfo === null) {
            return null
        }
        if (paymentInfo.IsCreditInvoice) {
            return 'Kreditfaktura'
        }
        if (paymentInfo.PaymentStatus === 0) {
            return 'Betald'
        }
        const tooltipText = formatCurrency(paymentInfo.RemainingAmount) + ' kr senast ' + paymentInfo.DueDate
        if (paymentInfo.PaymentStatus == 1) {
            return <span className="tooltip" title={tooltipText}>Obetald</span>
        }
        return <ReminderButton
            rowIndex={row.index}
            customerInvoiceId={paymentInfo.CustomerInvoiceId}
            alreadyIssued={paymentInfo.PaymentReminderIssued}
            tooltipText={tooltipText}
            customerName={row.original.CustomerName ?? 'kunden'}
            setIncomes={setIncomes}
        />
    }
)

const preprocessIncomes = (incomes_: Income[]) => {
    const accountDescriptionFormatter = createListFormatterPre<string>({textAlign: 'left', width: 'auto', elementFormatter: x => x})
    const incomeAmountFormatter = createListFormatterPre({textAlign: 'right', width: 'auto', elementFormatter: formatCurrency})
    const descriptionFormatter = createListFormatterPre<string>({textAlign: 'left', width: 400, elementFormatter: x => x})
    const incomes = incomes_.map(
        ({AccountDescription, IncomeAmount, Description, ...rest}) => ({
            AccountDescription: Object.assign([...AccountDescription], {cell: accountDescriptionFormatter(AccountDescription)}),
            IncomeAmount: Object.assign([...IncomeAmount], {cell: incomeAmountFormatter(IncomeAmount)}),
            Description: Object.assign(Description, {cell: descriptionFormatter(Description)}),
            ...rest
        }))
    return incomes
}

type PreprocessedIncome = ReturnType<typeof preprocessIncomes>[0]

const incomeColumnHelper = createMRTColumnHelper<PreprocessedIncome>();

const createIncomeColumns = ((setIncomes: (func: DraftFunction<Income[]>) => void) =>
    [
        incomeColumnHelper.accessor('VoucherDate',
            {
                header: 'Bokföringsdatum',
                //size: 'auto',
            }
        ),
        incomeColumnHelper.accessor('AccountDescription',
            {
                header: "Konto",
                size: 'auto' as unknown as number,
                Cell: ({cell}) => cell.getValue().cell, //createListFormatter({columnId: 'AccountDescription', textAlign: 'left', width: 'auto', elementFormatter: x => x}),
                sortingFn: categorySorter,
                filterFn: createListFilterFn(textFilter),
            }
        ),
        incomeColumnHelper.accessor('IncomeAmount',
            {
                header: "Belopp",
                size: 'auto' as unknown as number,
                //muiTableHeadCellProps: {
                //    align: 'right'
                //},
                Cell: ({cell}) => cell.getValue().cell, //createListFormatter({columnId: 'IncomeAmount', textAlign: 'right', width: 'auto', elementFormatter: formatCurrency}),
                sortingFn: sumSorter,
                filterFn: createListFilterFn(amountFilter)
            }
        ),
        incomeColumnHelper.accessor('Description',
            {
                header: "Fakturarader",
                size: 'auto' as unknown as number,
                Cell: ({cell}) => cell.getValue().cell, //createListFormatter({columnId: 'Description', textAlign: 'left', width: 400, elementFormatter: x => x}),
                sortingFn: firstSorter,
                filterFn: createListFilterFn(textFilter)
                //        {title:"Fakturarader", field:"Description", width:400, hozAlign:"left", formatter: formatList, sorter:firstSorter},
            }
        ),
        incomeColumnHelper.accessor((row) => row.CustomerName === null ? undefined: row.CustomerName,
            {
                header: 'Kund',
                id: 'CustomerName',
                size: 'auto' as unknown as number,
                sortUndefined: 'last',
                sortingFn: localeSortFn
                //        {title:"Kund", field:"CustomerName", sorter:"string", sorterParams:{locale:'sv-SE', alignEmptyValues:"bottom"}},
            }
        ),
        incomeColumnHelper.accessor('PaymentInfo',
            {
                header: "Betalinfo",
                Cell: createIncomePaymentInfoFormatter(setIncomes),
                sortingFn: incomePaymentInfoSorter
                //        {title:"Betalinfo", field:"PaymentInfo", hozAlign:"center", formatter: incomePaymentInfoFormatter, sorter: incomePaymentInfoSorter},
            }
        )
    ]
)

// Sorting and column filters held by the caller (Overview), so they survive the
// data reloads and selector changes that unmount and remount these tables. Passing
// them as controlled MRT state means the table re-applies them whenever it remounts.
export type ControlledTableState = {
    sorting: MRT_SortingState,
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>,
    columnFilters: MRT_ColumnFiltersState,
    setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>,
}

export function useControlledTableState(): ControlledTableState {
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
    return { sorting, setSorting, columnFilters, setColumnFilters }
}

const IncomeTable = memo( ({incomes, setIncomes, loading, tableState}: {incomes: PreprocessedIncome[], setIncomes: (func: DraftFunction<Income[]>) => void, loading: boolean, tableState: ControlledTableState}) => {
    const incomeTable = useMaterialReactTable({
        columns: createIncomeColumns(setIncomes),
        data: incomes, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
        initialState: {
            density: 'compact',
            pagination: {
              pageIndex: 0,
              pageSize: 30
            }
        },
        state: { sorting: tableState.sorting, columnFilters: tableState.columnFilters, showProgressBars: loading },
        onSortingChange: tableState.setSorting,
        onColumnFiltersChange: tableState.setColumnFilters,
        enableColumnResizing: false,
        layoutMode: 'semantic',
        // Keep a wide table inside the white surface: cap the paper at the container width so it
        // never spills onto the grey page, and let the table container scroll horizontally within it.
        muiTablePaperProps: { sx: { maxWidth: '100%' } },
        muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
        localization: MRT_Localization_SV,
        enableGlobalFilter: false,
        memoMode: 'rows'
      })

      return <MaterialReactTable table={incomeTable}/>
})

const expenseColumnHelper = createMRTColumnHelper<Expense>();

const expenseColumns =
    [
        expenseColumnHelper.accessor('VoucherDate',
            {
                header: 'Bokföringsdatum',
                size: 'auto' as unknown as number,
            }
        ),
        expenseColumnHelper.accessor("AccountDescription",
            {
                header: "Konto",
                size: 'auto' as unknown as number,
                Cell: createListFormatter<string>({textAlign: 'left', width: 'auto', elementFormatter: x => x}),
                sortingFn: categorySorter,
                filterFn: createListFilterFn(textFilter)
            }
        ),
        expenseColumnHelper.accessor("ExpenseAmount",
            {
                header: "Belopp",
                size: 'auto' as unknown as number,
                //muiTableHeadCellProps: {
                //    align: 'right'
                //},
                Cell: createListFormatter({textAlign: 'right', width: 'auto', elementFormatter: formatCurrency}),
                sortingFn: sumSorter,
                filterFn: createListFilterFn(amountFilter)
            }
        ),
        expenseColumnHelper.accessor("Description",
            {
                header: "Beskrivning",
                size: 'auto' as unknown as number,
                sortingFn: localeSortFn
            }
        ),
        expenseColumnHelper.accessor((row) => row.Message === null ? undefined: row.Message,
            {
                header: 'Meddelande',
                id: 'Message',
                size: 'auto' as unknown as number,
                sortUndefined: 'last',
                sortingFn: localeSortFn
            }
        ),
        expenseColumnHelper.accessor((row) => row.SupplierName === null ? undefined: row.SupplierName,
            {
                header: 'Betalningsmottagare',
                id: 'SupplierName',
                size: 'auto' as unknown as number,
                sortUndefined: 'last',
                sortingFn: localeSortFn
            }
        ),
        expenseColumnHelper.accessor("PaymentInfo",
            {
                header: "Betalinfo",
                Cell: expensePaymentInfoFormatter,
                sortingFn: expensePaymentInfoSorter
            }
        )
    ]

const ExpenseTable = memo( ({expenses, loading, tableState}: {expenses: Expense[], loading: boolean, tableState: ControlledTableState}) => {
    const expenseTable = useMaterialReactTable({
        columns: expenseColumns,
        data: expenses, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
        initialState: {
            density: 'compact',
            pagination: {
              pageIndex: 0,
              pageSize: 100
            }
        },
        state: { sorting: tableState.sorting, columnFilters: tableState.columnFilters, showProgressBars: loading },
        onSortingChange: tableState.setSorting,
        onColumnFiltersChange: tableState.setColumnFilters,
        enableColumnResizing: false,
        layoutMode: 'semantic',
        // Keep a wide table inside the white surface: cap the paper at the container width so it
        // never spills onto the grey page, and let the table container scroll horizontally within it.
        muiTablePaperProps: { sx: { maxWidth: '100%' } },
        muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
        localization: MRT_Localization_SV
      })

      return <MaterialReactTable table={expenseTable}/>
})

const OverviewContent = memo(({data, setData, loading, incomeTableState, expenseTableState}: {
    data: OverviewData,
    setData: (func: DraftFunction<OverviewData>) => void,
    loading: boolean,
    incomeTableState: ControlledTableState,
    expenseTableState: ControlledTableState,
}) => {
    const incomes = preprocessIncomes(data.incomes)
    const expenses = data.expenses

    const theme = useTheme();
    const theTheme = useMemo(() => createTheme(theme, svSE), [theme])

    // One self-contained block, so its spacing isn't also subject to the parent column's flex gap:
    // the two sections sit an even distance apart, each heading hugs its table, and the global
    // <h1>'s 1rem margins are reset here so they aren't double-counted against the gaps.
    return <ThemeProvider theme={theTheme}>
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 3, width: '100%', '& h1': { m: 0 } }}>
                <Box sx={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                    <h1>Intäkter</h1>
                    <IncomeTable incomes={incomes} setIncomes={(func: DraftFunction<Income[]>) => {setData((data) => {func(data.incomes)})}} loading={loading} tableState={incomeTableState} />
                </Box>
                <Box sx={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                    <h1>Kostnader</h1>
                    <ExpenseTable expenses={expenses} loading={loading} tableState={expenseTableState} />
                </Box>
            </Box>
        </ThemeProvider>
}
)

export default OverviewContent
